import { ChevronRight, Plus } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export type IntegrationStatus = 'active' | 'connected' | 'disconnected';

export interface IntegrationCardProps {
    name: string;
    logo?: React.ReactNode;
    status: IntegrationStatus;
    description: string;
    dataPoints: string[];
    isPrimary?: boolean;
    isComingSoon?: boolean;
    onAction?: () => void;
}

export function cn(...inputs: (string | undefined | null | false)[]) {
    return twMerge(clsx(inputs));
}

// Integration Card Component
export const IntegrationCard: React.FC<IntegrationCardProps> = ({
    name,
    logo,
    status,
    description,
    isPrimary = false,
    isComingSoon = false,
    onAction
}) => {

    // Status visual configurations
    const statusConfig = {
        active: {
            color: 'bg-green-500',
            textColor: 'text-green-600',
            label: 'Activa',
            borderColor: 'border-green-200',
            bg: 'bg-green-50'
        },
        connected: {
            color: 'bg-yellow-500',
            textColor: 'text-yellow-600',
            label: 'Conectada / Sin uso',
            borderColor: 'border-yellow-200',
            bg: 'bg-yellow-50'
        },
        disconnected: {
            color: 'bg-red-500',
            textColor: 'text-red-600',
            label: 'No conectada',
            borderColor: 'border-red-200',
            bg: 'bg-red-50'
        }
    };

    const currentStatus = statusConfig[status];

    if (isComingSoon) {
        return (
            <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-6 opacity-70 transition-all hover:opacity-80">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white shadow-sm grayscale">
                            {logo || <div className="h-6 w-6 rounded-full bg-gray-300" />}
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-500">{name}</h3>
                            <span className="inline-flex items-center rounded-full bg-gray-200 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                                Próximamente
                            </span>
                        </div>
                    </div>
                </div>
                <div className="mt-4">
                    <p className="text-sm text-gray-500">
                        Disponible en próximas etapas.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={cn(
            "group relative flex flex-col justify-between overflow-hidden rounded-xl border p-6 transition-all duration-300",
            "hover:shadow-lg hover:border-violet-200 bg-white",
            isPrimary ? "border-violet-100 ring-1 ring-violet-100 ring-offset-2" : "border-gray-100"
        )}>
            {/* Primary Badge */}
            {isPrimary && (
                <div className="absolute top-0 right-0 rounded-bl-xl bg-violet-600 px-3 py-1 text-xs font-medium text-white shadow-sm">
                    Fuente base
                </div>
            )}

            <div>
                {/* Header */}
                <div className="flex items-start gap-4 mb-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm border border-gray-100">
                        {logo || <div className="h-8 w-8 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center font-bold">{name.charAt(0)}</div>}
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 leading-tight">{name}</h3>

                        <div className="mt-1 flex items-center gap-2">
                            <span className={cn("relative flex h-2.5 w-2.5 rounded-full", currentStatus.color)}>
                                {status === 'active' && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>}
                            </span>
                            <span className={cn("text-xs font-medium", currentStatus.textColor)}>
                                {currentStatus.label}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <p className="mb-4 text-sm text-gray-600 leading-relaxed">
                    {description}
                </p>
            </div>

            {/* Action Footer */}
            <div className="mt-auto pt-4 border-t border-gray-100">
                {status === 'active' || status === 'connected' ? (
                    <div className="flex gap-2">
                        <button
                            onClick={onAction}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-violet-50 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-100 transition-colors"
                        >
                            Ver detalles
                            <ChevronRight size={16} />
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={onAction}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white shadow-md shadow-violet-200 hover:bg-violet-700 hover:shadow-lg transition-all"
                    >
                        <Plus size={16} />
                        Conectar
                    </button>
                )}
            </div>
        </div>
    );
};
